import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MessageCircle,
  MessageSquare,
  ShoppingCart,
  Settings,
  User,
  Bot,
  BadgeQuestionMark as Help,
  Info,
  Phone,
  Mail,
  Home,
  X,
  Moon,
  Sun,
  Languages,
  ChevronDown,
} from 'lucide-react';
import { useTheme } from 'chat-interface-lib';
import { useI18n } from '../contexts/I18nProvider';
import type { AgentTranslation, IconName } from '../types';
import { apiService } from '../services/api';
import { useTenantIcons } from '../hooks/useTenantIcons';
import * as LucideIcons from 'lucide-react';

interface AgentSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const AgentSidebar: React.FC<AgentSidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [agents, setAgents] = useState<Array<{ id: string; name: string; description: string; icon: string; color: string, translations?: AgentTranslation[] }>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { theme, toggleTheme } = useTheme();
  const { currentLanguage, availableLanguages, changeLanguage, isLoading: i18nLoading } = useI18n();
  const [languages, setLanguages] = useState<Array<{ id: string; code: string; isDefault?: boolean }>>([]);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  // Get tenant icons
  const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'default';
  const { getIcon } = useTenantIcons(tenantSlug);

  // Get icon components
  const HomeIcon = getIcon('home', 'Home') || LucideIcons.Home;
  const SettingsIcon = getIcon('settings', 'Settings') || LucideIcons.Settings;
  const UserIcon = getIcon('user', 'User') || LucideIcons.User;
  const XIcon = X; // Close icon
  const MoonIcon = getIcon('themeToggle', 'Moon') || LucideIcons.Moon;
  const SunIcon = Sun; // Always use Sun for light mode
  const LanguageIcon = getIcon('language', 'Languages') || LucideIcons.Languages;
  const ChevronDownIcon = getIcon('chevronDown', 'ChevronDown') || LucideIcons.ChevronDown;
  const MessageCircleIcon = getIcon('messageCircle', 'MessageCircle') || LucideIcons.MessageCircle;
  const MessageSquareIcon = getIcon('messageSquare', 'MessageSquare') || LucideIcons.MessageSquare;
  const ShoppingCartIcon = getIcon('shoppingCart', 'ShoppingCart') || LucideIcons.ShoppingCart;
  const HelpIcon = getIcon('help', 'HelpCircle') || Help;
  const InfoIcon = getIcon('info', 'Info') || LucideIcons.Info;
  const PhoneIcon = getIcon('phone', 'Phone') || LucideIcons.Phone;
  const MailIcon = getIcon('mail', 'Mail') || LucideIcons.Mail;

  // Icon mapping for agent icons
  const iconMap: Record<IconName, React.ComponentType<any>> = {
    MessageCircle: MessageCircleIcon,
    MessageSquare: MessageSquareIcon,
    ShoppingCart: ShoppingCartIcon,
    Settings: SettingsIcon,
    User: UserIcon,
    Bot: getIcon('bot', 'Bot') || LucideIcons.Bot,
    Help: HelpIcon,
    Info: InfoIcon,
    Phone: PhoneIcon,
    Mail: MailIcon,
  };

  const isActive = (agentId: string) => location.pathname === `/agent/${agentId}`;
  const isHome = location.pathname === '/';

  const handleAgentSelect = (agentId: string) => {
    navigate(`/agent/${agentId}`);
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleLanguageChange = async (language: string) => {
    try {
      await changeLanguage(language);
      setIsLanguageDropdownOpen(false);
    } catch (error) {
      console.error('Language change failed:', error);
    }
  };

  useEffect(() => {
    const loadAgents = async () => {
      const user = apiService.getStoredUser();
      if (!user?.tenantId) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }
      try {
        const languages = await apiService.getTenantLanguages(user.tenantId)
        setLanguages(languages);
        setLoading(true);
        setError(null);
        const list = await apiService.getTenantAgents(user.tenantId);
        setAgents(list);
      } catch (e) {
        setError('Failed to load agents');
      } finally {
        setLoading(false);
      }
    };
    loadAgents();
  }, [currentLanguage]);

  return (
    <div className={`bg-card border-r border-border shadow-lg transition-all duration-300 ease-in-out ${
      isOpen ? 'w-80' : 'w-0'
    } overflow-hidden h-screen`}>
      <div className="flex flex-col h-full w-80">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {t('common.multiAgentChat')}
          </h2>
          <div className="flex items-center space-x-2">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center space-x-1 p-2 hover:bg-muted rounded-md transition-colors"
                title={t('common.selectLanguage')}
                disabled={i18nLoading}
              >
                <LanguageIcon className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {availableLanguages[currentLanguage] || currentLanguage}
                </span>
                <ChevronDownIcon className={`w-3 h-3 text-muted-foreground transition-transform duration-200 ${
                  isLanguageDropdownOpen ? 'rotate-180' : ''
                }`} />
              </button>

              {/* Language Dropdown Menu */}
              {isLanguageDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-50 py-1">
                  <div className="max-h-48 overflow-y-auto">
                    {Object.entries(availableLanguages).map(([langCode, langName]) => (
                      <button
                        key={langCode}
                        onClick={() => handleLanguageChange(langCode)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                          currentLanguage === langCode ? 'bg-accent text-accent-foreground' : 'text-foreground'
                        }`}
                      >
                        {langName}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-muted rounded-md transition-colors"
              title={theme === 'dark' ? t('common.switchToLightMode') : t('common.switchToDarkMode')}
            >
              {theme === 'dark' ? (
                <SunIcon className="w-5 h-5 text-muted-foreground" />
              ) : (
                <MoonIcon className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            <button
              onClick={onToggle}
              className="p-2 hover:bg-muted rounded-md transition-colors"
              title={t('common.close')}
            >
              <XIcon className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-3 overflow-y-auto">
          {/* Home Button */}
          <button
            onClick={handleHomeClick}
            className={`w-full flex items-start space-x-3 p-4 rounded-lg transition-all duration-200 text-left border ${
              isHome
                ? 'bg-primary text-primary-foreground shadow-sm border-primary'
                : 'hover:bg-muted text-foreground border-transparent hover:border-border'
            }`}
          >
            <HomeIcon className={`w-5 h-5 mt-0.5 ${
              isHome ? 'text-primary-foreground' : 'text-muted-foreground'
            }`} />
            <div className="flex-1 min-w-0">
              <div className={`font-medium ${
                isHome ? 'text-primary-foreground' : 'text-foreground'
              }`}>
                {t('common.home')}
              </div>
              <div className={`text-sm ${
                isHome ? 'text-primary-foreground/80' : 'text-muted-foreground'
              }`}>
                {t('common.agentSelection')}
              </div>
            </div>
          </button>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Agent List */}
          {agents.map((agent) => {
            const IconComponent = iconMap[agent.icon as IconName] || MessageCircleIcon;
            const currentLanguageId = languages.find(l => l.code === currentLanguage || l.isDefault)?.id;
            const agentTranslation = agent.translations?.find(t => t.languageId === currentLanguageId);
            const agentName = agentTranslation?.name || agent.name;
            const agentDescription = agentTranslation?.description || agent.description;

            
            return (
              <button
                key={agent.id}
                onClick={() => handleAgentSelect(agent.id)}
                className={`w-full flex items-start space-x-4 p-4 rounded-lg transition-all duration-200 text-left border group ${
                  isActive(agent.id)
                    ? 'bg-primary text-primary-foreground shadow-sm border-primary'
                    : 'hover:bg-muted text-foreground border-transparent hover:border-border bg-card'
                }`}
                style={{
                  // Use the agent's actual color configuration when not active
                  ...(isActive(agent.id) ? {} : {
                    borderColor: `${agent.color}30`, // 30 = 18% opacity
                    backgroundColor: `${agent.color}08` // 08 = 3% opacity
                  })
                }}
              >
                <div 
                  className={`p-2 rounded-lg ${
                    isActive(agent.id) 
                      ? 'bg-primary-foreground/20' 
                      : 'bg-background/50 group-hover:bg-background/70'
                  }`}
                  style={{
                    // Use the agent's color for the icon background when not active
                    ...(isActive(agent.id) ? {} : {
                      backgroundColor: `${agent.color}20`, // 20 = 12% opacity
                      borderColor: agent.color
                    })
                  }}
                >
                  <IconComponent 
                    className="w-5 h-5"
                    style={{
                      // Use the agent's color for the icon when not active
                      color: isActive(agent.id) ? undefined : agent.color
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium ${
                    isActive(agent.id) ? 'text-primary-foreground' : 'text-foreground'
                  }`}>
                    {agentName}
                  </div>
                  <div className={`text-sm leading-relaxed ${
                    isActive(agent.id) ? 'text-primary-foreground/80' : 'text-muted-foreground'
                  }`}>
                    {agentDescription}
                  </div>
                </div>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <div className="text-xs text-muted-foreground text-center">
            {agents.length} {agents.length === 1 ? t('common.agent') : t('common.agents_plural')} {t('common.available')}
          </div>
        </div>
      </div>
    </div>
  );
};